// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SubReportBody.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * SubReportBody Component
 */

import localization from 'share/localization/Izenda.Localizer';
import React from 'react';
import m from 'mobx';
import { observer } from 'mobx-react';
import EventsUtils from 'EventUtils';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import DropDown from 'share/components/controls/Izenda.DropDown';
import SubReportGrid from './Izenda.SubReportGrid';
import Icon from 'share/components/controls/Izenda.Icon';
import ReportPartSelectionConstants from 'logic/data/Izenda.ReportPartSelectionConstants';
import ReportPartSelectionPopup from 'share/components/controls/ReportPartSelectionPopup/Izenda.ReportPartSelectionPopup';
import PropertiesBuilderConstants from 'logic/data/Izenda.PropertiesBuilderConstants';
import reportDesignerContants from 'logic/data/Izenda.ReportDesignerConstants';
import SubReportModel from 'logic/models/Izenda.SubReport';
import ViewSetting from './Izenda.ViewSetting';
import { isNil, isEmpty, getOr } from 'lodash/fp';

const ValidatableMultiSelectDropDown = Validator()(Select);
const ValidatableDropDown = Validator()(DropDown);
const constants = PropertiesBuilderConstants.SUB_REPORT;
const messages = PropertiesBuilderConstants.MESSAGE;

const ViewSettingContainer = observer(({ model, hiddenIconSettingSubReport }) => {
  return model.style !== reportDesignerContants.DRIL_DOWN_STYLE.EMBEDDED && !hiddenIconSettingSubReport ? (
    <ViewSetting
      viewSettingByLink={model.viewSettingByLink}
      selectedIconValue={model.selectedIconValue}
      updateSelectedViewSettingIcon={model.updateSelectedViewSettingIcon.bind(model)}
      updateViewSettingByLink={model.updateViewSettingByLink.bind(model)}
    />
  ) : null;
});

ViewSettingContainer.displayName = 'ViewSettingContainer';

const StyleDropdown = observer(({ model, stypeOptions }) => (
  <div className='input-group col-md-12'>
    <label className='col-md-2'>{localization.getText('COMMON_LABEL_STYLE')}</label>
    <div className='col-md-10 '>
      <ValidatableDropDown
        className='col-md-12 no-padding'
        options={stypeOptions}
        onChange={EventsUtils.notifyChange(model.updateStyle.bind(model), EventsUtils.DROPDOWN)}
        value={model.style}
        validations={constants.CONSTRAINTS.STYLE_CONSTRAINTS}
        field='style'
      />
    </div>
  </div>
));

StyleDropdown.displayName = 'StyleDropdown';

const ReportPartUsedDropdown = observer(({ model }) => {
  let options = m.toJS(model.reportParts);

  // When value = all , clear out all option
  if (model.reportPartUsed == constants.ALL_OPTION.title) {
    options = [constants.ALL_OPTION];
  } else {
    // Add All option
    !isEmpty(options) && options.splice(0, 0, constants.ALL_OPTION);
  }

  return model.style === reportDesignerContants.DRIL_DOWN_STYLE.EMBEDDED ? (
    <div className='input-group col-md-12'>
      <label className='col-md-2'>{localization.getText('REPORTDESIGNER_LABEL_REPORT_PART_USED')}</label>
      <div className='col-md-10 '>
        <ValidatableMultiSelectDropDown
          className='col-md-12 no-padding'
          options={options}
          labelKey='title'
          valueKey='id'
          disabled={isNil(model.selectedReport) || isEmpty(model.selectedReport)}
          onChange={model.updateReportPart.bind(model)}
          value={model.reportPartUsed}
          blankOption
          validations={constants.CONSTRAINTS.REPORT_PART_USED_CONSTRAINTS}
          field='reportParts'
          allowCreate={false}
          multi
        />
      </div>
    </div>
  ) : null;
});

ReportPartUsedDropdown.displayName = 'ReportPartUsedDropdown';

const ReportFilterCheckbox = observer(({ model }) => (
  <div className='reportFilters col-md-12'>
    <div className='col-md-2' />
    <div className='col-md-10'>
      <input
        type='checkbox'
        checked={model.reportFilter}
        onChange={EventsUtils.notifyChange(model.updateReportFilter.bind(model), EventsUtils.CHECKBOX)}
      />
      <span>{constants.SUBREPORT_FILTER_TEXT}</span>
    </div>
  </div>
));

ReportFilterCheckbox.displayName = 'ReportFilterCheckbox';

@observer
class ReportDropdown extends React.Component {
  static displayName = 'ReportDropdown';
  openSearchReportPopup = () => {
    const { reportId } = this.props.fieldStore;
    this.props.openPopup(ReportPartSelectionPopup, {
      onChange: this.changeReport,
      type: ReportPartSelectionConstants.TYPE.REPORT,
      excludeIds: isNil(reportId) ? [] : [reportId]
    });
  };

  changeReport = (result: Object) => {
    let model = this.props.model;
    let { fieldStore: store, notifySettingButtonState } = this.props;
    let report = getOr({}, 'report', result);
    let reportId = report.id || null;

    model.updateSelectedReport(report);
    store.loadSubReportData(reportId).then(model.readSubReport.bind(model));
    notifySettingButtonState(model.ADD_MAPPING_FIELD_BUTTON, !reportId);
  };

  render() {
    let model = this.props.model;
    return (
      <div className='form-group col-md-12'>
        <label className='col-md-2'>{localization.getText('COMMON_LABEL_REPORTS')}</label>
        <div className='col-md-9'>
          <Input value={model.selectedReportName} className='form-control' disabled />
        </div>
        <div className='col-md-1'>
          <Icon className='icon-search' onClick={this.openSearchReportPopup} />
          <Icon className='icon-cancel' onClick={this.changeReport} />
        </div>
      </div>
    );
  }
}

@observer
class SubReportBody extends React.Component {
  static propTypes = {
    fieldStore: React.PropTypes.object,
    stypeOptions: React.PropTypes.object.isRequired
  };

  static displayName = 'SubReportBody';

  getValue = () => this.model;

  componentWillMount() {
    this.model = new SubReportModel();
  }

  componentDidMount() {
    let { value = {}, fieldStore: store, loader, hiddenIconSettingSubReport } = this.props;
    let model = this.model;

    loader.startLoading();
    Promise.all([
      model.readInitialData(value, hiddenIconSettingSubReport),
      store.loadInitialSubReports(value.selectedReport).then(model.readReports.bind(model))
    ]).finally(loader.endLoading.bind(loader));
  }

  addMappingField = () => {
    this.model.addMappingField();
  };

  beforeClosing = (valManager: Object, type: String) => {
    let { checkUniqLinkOption, confirmMessage, checkEmbeddedLinkOption, confirmFn } = this.props;
    let model = this.model;

    // Remove empty mapping item
    model.removeUnusedMappingField();

    // Check view option
    if (type === 'accept') {
      let errors = model.validateDuplicate();

      if (!isEmpty(errors)) {
        valManager.updateErrors(errors);

        return Promise.resolve(false);
      }

      // Check uniq link option
      if (
        model.style !== reportDesignerContants.DRIL_DOWN_STYLE.EMBEDDED &&
        checkUniqLinkOption &&
        !checkUniqLinkOption(model, constants.NAME)
      ) {
        return confirmMessage('error', messages.DUPLICATE_VIEW_SETTING);
      }

      // Check embedded sub report
      if (
        model.style === reportDesignerContants.DRIL_DOWN_STYLE.EMBEDDED &&
        checkEmbeddedLinkOption &&
        !checkEmbeddedLinkOption(model.style)
      ) {
        return confirmFn('Confirmation', {
          description: messages.EMBEDDED_LINK_SUB_REPORT
        }).return(false);
      }
    }
    return Promise.resolve(true);
  };

  /**
   * [generateDataForReportTreeDropDownList this function is to prepare data to render
   * @return {[type]} list of report changed the structure
   */
  render() {
    return (
      <section className='subReport inline-block u_fullWidth'>
        <ReportDropdown
          model={this.model}
          fieldStore={this.props.fieldStore}
          openPopup={this.props.openPopup}
          notifySettingButtonState={this.props.notifySettingButtonState}
        />
        <ReportFilterCheckbox model={this.model} />
        <SubReportGrid model={this.model} />
        <StyleDropdown model={this.model} stypeOptions={this.props.stypeOptions} />
        <ReportPartUsedDropdown model={this.model} />
        <ViewSettingContainer model={this.model} hiddenIconSettingSubReport={this.props.hiddenIconSettingSubReport} />
      </section>
    );
  }
}

export default SubReportBody;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.SubReportBody.js